import { Link, useLocation } from "react-router-dom";
import { Linkedin, Mail, MapPin, Zap, ArrowUpRight, Heart } from "lucide-react";

export default function Footer() {
  const year = new Date().getFullYear();
  const { pathname } = useLocation();
  const isHomePage = pathname === "/";

  return (
    <footer 
      data-testid="site-footer" 
      className={`relative bg-[#0A0A0B] overflow-hidden pb-10 ${isHomePage ? "pt-0" : "pt-24 border-t border-white/5"}`}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-10 pt-16">
          <div className="col-span-1 md:col-span-5">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 group">
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all">
                  <Zap className="w-5 h-5 text-black" strokeWidth={2.5} />
                </span>
                <span className="text-2xl font-bold tracking-tight">Moxizo<span className="text-cyan-400">.</span></span>
              </Link>
              <div className="h-6 w-[1px] bg-white/10" />
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noreferrer"
                className="text-gray-400 hover:text-cyan-400 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
            <p className="mt-6 text-base text-gray-400 leading-relaxed max-w-sm">
              Your strategic partner in telecom evolution. From BSS transformation to 24x7 managed services, we deliver precision at scale.
            </p>
            
            {/* Desktop Badge */}
            <div className="hidden md:flex mt-8">
              <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-white/[0.03] border border-white/5 rounded-full text-sm text-gray-400 backdrop-blur-sm">
                <span className="w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center">
                  <Heart className="w-3 h-3 text-red-500 fill-red-500" />
                </span>
                <span className="text-[13px]">
                  Designed by <span className="font-semibold text-white">ADITYA </span>
                  <a href="https://adityazen.online" target="_blank" rel="noreferrer" className="font-semibold text-white hover:text-cyan-400 transition-colors underline decoration-cyan-400/30 underline-offset-4">Zen</a>
                </span>
              </div>
            </div>
          </div>

          <div className="col-span-1 md:col-span-3">
            <p className="text-sm font-bold tracking-widest uppercase text-white mb-8">Navigation</p>
            <ul className="space-y-4 text-base text-gray-400">
              {["Home","About","Services","Blog","Contact"].map((l) => (
                <li key={l}>
                  <Link
                    to={l === "Home" ? "/" : `/${l.toLowerCase()}`}
                    className="hover:text-cyan-400 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-0 h-[1px] bg-cyan-400 group-hover:w-3 transition-all" />
                    {l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1 md:col-span-4">
            <p className="text-sm font-bold tracking-widest uppercase text-white mb-8">Get in touch</p>
            <ul className="space-y-6 text-base text-gray-400">
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-cyan-400">
                  <Mail className="w-4 h-4" />
                </div>
                <a href="mailto:support@moxizo.com" className="hover:text-white pt-2">support@moxizo.com</a>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-cyan-400">
                  <MapPin className="w-4 h-4" />
                </div>
                <span className="pt-1">401, Synergy, Nagpur,<br />Maharashtra 440008, India</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Mobile Badge */}
        <div className="flex md:hidden justify-center mt-12 mb-4">
          <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-white/[0.03] border border-white/5 rounded-full text-sm text-gray-400 backdrop-blur-sm">
            <span className="w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center">
              <Heart className="w-3 h-3 text-red-500 fill-red-500" />
            </span>
            <span className="text-[13px]">
              Designed by <span className="font-semibold text-white">ADITYA </span>
              <a href="https://adityazen.online" target="_blank" rel="noreferrer" className="font-semibold text-white hover:text-cyan-400 transition-colors underline decoration-cyan-400/30 underline-offset-4">Zen</a>
            </span>
          </div>
        </div>

        <div className="mt-16 md:mt-24 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-gray-500">
          <p>© {year} Moxizo Consulting. All rights reserved.</p>
          <div className="flex items-center gap-8">
            <Link to="#" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
